import { useEffect, useState } from "react"
import { Copy, Settings2, Trash2, UserPlus } from "lucide-react"
import { toast } from "sonner"
import { InviteModal } from "@/components/InviteModal"
import { PermissionEditorModal } from "@/components/PermissionEditorModal"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { supabase } from "@/lib/supabase"
import { formatDate } from "@/lib/format"
import { usePermission } from "@/hooks/usePermission"
import { useAuthStore } from "@/stores/authStore"
import { useOrgStore } from "@/stores/orgStore"
import type { Json } from "@/types/database"

type MemberRole = "owner" | "admin" | "editor" | "viewer"

interface TeamMember {
  id: string
  role: MemberRole
  created_at: string
  user_id: string
  user_email: string | null
  permissions: Json | null
}

interface PendingInvite {
  id: string
  email: string
  role: MemberRole
  expires_at: string
  created_at: string
  token: string
  invited_by_email: string | null
}

function getRoleBadgeClass(role: MemberRole): string {
  switch (role) {
    case "owner":
      return "bg-blue-100 text-blue-800 border-blue-200"
    case "admin":
      return "bg-purple-100 text-purple-800 border-purple-200"
    case "editor":
      return "bg-green-100 text-green-800 border-green-200"
    case "viewer":
      return "bg-gray-100 text-gray-800 border-gray-200"
    default:
      return "bg-gray-100 text-gray-800 border-gray-200"
  }
}

function formatExpiresAt(dateString: string): string {
  const expiresAt = new Date(dateString)
  const now = new Date()
  const diffMs = expiresAt.getTime() - now.getTime()
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24))

  if (diffDays <= 0) {
    return "Expired"
  } else if (diffDays === 1) {
    return "Expires tomorrow"
  } else {
    return `Expires in ${diffDays} days`
  }
}

export function TeamPage() {
  const { user } = useAuthStore()
  const { currentOrg } = useOrgStore()
  const [members, setMembers] = useState<TeamMember[]>([])
  const [invites, setInvites] = useState<PendingInvite[]>([])
  const [loading, setLoading] = useState(true)
  const [inviteModalOpen, setInviteModalOpen] = useState(false)

  const fetchTeamData = async () => {
    if (!currentOrg?.id) return

    setLoading(true)

    // Fetch members
    const { data: membersData, error: membersError } = await supabase
      .from("organization_members")
      .select("id, role, created_at, user_id, permissions")
      .eq("org_id", currentOrg.id)
      .order("created_at", { ascending: true })

    if (membersError) {
      toast.error("Failed to load team members")
      console.error(membersError)
    } else if (membersData) {
      const membersWithEmails: TeamMember[] = membersData.map((m) => ({
        id: m.id,
        role: m.role as MemberRole,
        created_at: m.created_at,
        user_id: m.user_id,
        user_email: m.user_id === user?.id ? user?.email ?? null : null,
        permissions: m.permissions ?? null,
      }))

      // Try to fetch emails for all members using their IDs
      // This requires a custom RPC or view - for now we'll just show what we have
      setMembers(membersWithEmails)
    }

    // Fetch pending invites
    const { data: invitesData, error: invitesError } = await supabase
      .from("invites")
      .select("id, email, role, expires_at, created_at, token, invited_by")
      .eq("org_id", currentOrg.id)
      .is("accepted_at", null)
      .gt("expires_at", new Date().toISOString())
      .order("created_at", { ascending: false })

    if (invitesError) {
      toast.error("Failed to load pending invites")
      console.error(invitesError)
    } else if (invitesData) {
      const invitesWithEmails: PendingInvite[] = invitesData.map((i) => ({
        id: i.id,
        email: i.email,
        role: i.role as MemberRole,
        expires_at: i.expires_at,
        created_at: i.created_at,
        token: i.token,
        invited_by_email: i.invited_by === user?.id ? user?.email ?? null : null,
      }))
      setInvites(invitesWithEmails)
    }

    setLoading(false)
  }

  useEffect(() => {
    fetchTeamData()
  }, [currentOrg?.id, user?.id])

  const { hasPermission: canManageTeam } = usePermission("can_manage_team")
  const canInvite = canManageTeam
  const canEditPermissions = canManageTeam

  const handleCopyInviteLink = async (token: string) => {
    const inviteUrl = `${window.location.origin}/accept-invite?token=${token}`
    try {
      await navigator.clipboard.writeText(inviteUrl)
      toast.success("Invite link copied to clipboard")
    } catch {
      toast.error("Failed to copy link")
    }
  }

  const handleRemoveMember = async (memberId: string, memberEmail: string | null) => {
    if (!confirm(`Remove ${memberEmail || "this member"} from this organization?`)) return

    const { error } = await supabase
      .from("organization_members")
      .delete()
      .eq("id", memberId)

    if (error) {
      toast.error("Failed to remove member")
      console.error(error)
      return
    }

    toast.success("Member removed")
    fetchTeamData()
  }

  const [permissionEditorMember, setPermissionEditorMember] =
    useState<TeamMember | null>(null)

  const handleRevokeInvite = async (inviteId: string, email: string) => {
    if (!confirm(`Revoke invite for ${email}?`)) return

    const { error } = await supabase
      .from("invites")
      .delete()
      .eq("id", inviteId)

    if (error) {
      toast.error("Failed to revoke invite")
      console.error(error)
      return
    }

    toast.success("Invite revoked")
    fetchTeamData()
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <p className="text-muted-foreground">Loading team...</p>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Team Members</h1>
          <p className="text-muted-foreground">
            Manage your organization's team members and invites
          </p>
        </div>
        {canInvite && (
          <Button onClick={() => setInviteModalOpen(true)}>
            <UserPlus className="mr-2 h-4 w-4" />
            Invite Member
          </Button>
        )}
      </div>

      {/* Current Members */}
      <Card>
        <CardHeader>
          <CardTitle>Members</CardTitle>
          <CardDescription>
            {members.length} member{members.length !== 1 ? "s" : ""} in this organization
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Member</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Joined</TableHead>
                {canEditPermissions && <TableHead className="w-[120px]">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {members.map((member) => (
                <TableRow key={member.id}>
                  <TableCell className="font-medium">
                    {member.user_email || member.user_id.slice(0, 8) + "..."}
                    {member.user_id === user?.id && (
                      <span className="ml-2 text-xs text-muted-foreground">(you)</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge className={getRoleBadgeClass(member.role)}>
                      {member.role.charAt(0).toUpperCase() + member.role.slice(1)}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatDate(member.created_at)}
                  </TableCell>
                  {canEditPermissions && (
                    <TableCell>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setPermissionEditorMember(member)}
                          title="Edit permissions"
                        >
                          <Settings2 className="h-4 w-4" />
                        </Button>
                        {member.user_id !== user?.id && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleRemoveMember(member.id, member.user_email)}
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Pending Invites */}
      {canInvite && (
        <Card>
          <CardHeader>
            <CardTitle>Pending Invites</CardTitle>
            <CardDescription>
              {invites.length} pending invite{invites.length !== 1 ? "s" : ""}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {invites.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">
                No pending invites. Click "Invite Member" to add team members.
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Invited By</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-[120px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invites.map((invite) => (
                    <TableRow key={invite.id}>
                      <TableCell className="font-medium">{invite.email}</TableCell>
                      <TableCell>
                        <Badge className={getRoleBadgeClass(invite.role)}>
                          {invite.role.charAt(0).toUpperCase() + invite.role.slice(1)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {invite.invited_by_email || "Team member"}
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {formatExpiresAt(invite.expires_at)}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleCopyInviteLink(invite.token)}
                            title="Copy invite link"
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleRevokeInvite(invite.id, invite.email)}
                            className="text-destructive hover:text-destructive"
                            title="Revoke invite"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      <InviteModal
        open={inviteModalOpen}
        onOpenChange={setInviteModalOpen}
        onInviteCreated={fetchTeamData}
      />

      {permissionEditorMember && (
        <PermissionEditorModal
          open={!!permissionEditorMember}
          onOpenChange={(open) => !open && setPermissionEditorMember(null)}
          memberId={permissionEditorMember.id}
          memberLabel={
            permissionEditorMember.user_email ||
            permissionEditorMember.user_id.slice(0, 8) + "..."
          }
          role={permissionEditorMember.role}
          permissions={permissionEditorMember.permissions}
          onSaved={fetchTeamData}
        />
      )}
    </div>
  )
}
